import ApiFetchers from "@/apis/api_fetchers"
import { BusinessSignals } from "@/apis/api_signals"
import { ModalSlideUpStaticMethods } from "@/components/composite/ModalSlideUp/main"
import { createLogger } from "@/utils/logger"
import { signal, computed } from "@preact/signals"
import KycWarningWithCountdown from "@/components/kyc/KycWarningWithCountdown"
import KycReminderHint from "@/components/kyc/KycReminderHint"
import KycReminderInUserCenter from "@/components/kyc/KycReminderInUserCenter"
import KycReminderInDeposit from "@/components/kyc/KycReminderInDeposit"
import KycReminderInBeforeGame from "@/components/kyc/KycReminderInBeforeGame"
import { clearSearchAction } from "@/utils/navigation"
import KycReminderInWithdraw from "@/components/kyc/KycReminderInWithdraw"
import KycReminderHintBlockAccountLogin from "@/components/kyc/KycReminderHintBlockAccountLogin"
import KycReminderHintUnderReview from "@/components/kyc/KycReminderHintUnderReview"
import KycReminderInWelfare from "@/components/kyc/KycReminderInWelfare"

const logger = createLogger("kycRequired")

// 源數據
export const sourceData = BusinessSignals.kycSourceData

// 定義可能需要 KYC 的動作類型
export type KycRequiredAction =
    | "updateProfile" // 修改用戶資料 (username/密碼)
    | "deposit" // 充值
    | "depositmaya" //mayamini充值
    | "withdraw" // 提現
    | "play" // 進入遊戲
    | "welfare" // 參與活動
    | "viewRecords" // 查看記錄
    | "afterLogin" // 登入後檢查

type Rule = {
    byPassStatus: Kyc.Status[]
    byPassCallback?: (kycStatus: Kyc.Status) => boolean
}

// KYC 狀態要求
export const kycRulesConfig = signal<Record<KycRequiredAction, Rule>>({
    // 修改用戶資料 (username/密碼)
    updateProfile: {
        byPassStatus: [
            "basic_account",
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "frozen",
            "test_account",
            // "block" 不允許修改
        ],
    },
    // 充值功能
    deposit: {
        byPassStatus: [
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "test_account",
            // "basic_account", "frozen", "block" 不允許充值
        ],
    },
    // mayamini充值功能
    depositmaya: {
        byPassStatus: [
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "test_account",
            // "basic_account", "frozen", "block" 不允許充值
        ],
    },
    // 提現功能
    withdraw: {
        byPassStatus: [
            "fully_kyc",
            "test_account",
            // 其他狀態都不允許提現
        ],
    },
    // 進入遊戲
    play: {
        byPassStatus: [
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "test_account",
            // "basic_account", "frozen", "block" 不允許進入遊戲
        ],
    },
    // 參與活動
    welfare: {
        byPassStatus: [
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "test_account",
            // "basic_account", "frozen", "block" 不允許參與活動
        ],
    },
    // 查看記錄 (充值記錄、提款記錄、下注記錄等)
    viewRecords: {
        byPassStatus: [
            "fully_kyc",
            "frozen",
            "test_account",
            // 其他狀態不允許查看記錄
        ],
    },
    // 登入後檢查
    afterLogin: {
        byPassStatus: [
            "basic_account",
            "under_review",
            "rejected",
            "basic_kyc",
            "fully_kyc",
            "frozen",
            "test_account",
            // "block" 不允許登陸後續操作
        ],
    },
})
export const currentKycStatus = computed(() => {
    const { kyc } = sourceData.value
    if (kyc?.status) return kyc.status
    return "unset" as Kyc.Status
})

// 當前執行的動作 ID
export const currentActionId = signal<number>(0)

// 當前被阻止的動作
export const blockedAction = signal<{
    id: number
    type: KycRequiredAction
} | null>(null)

interface ExecuteStatus {
    id: number
    allowed: boolean
    currentStatus: Kyc.Status
    requiredStatus: Kyc.Status[]
    byPassCallback?: (status: ExecuteStatus) => Promise<boolean>
    action: KycRequiredAction
    sourceData: typeof sourceData.value
}

const openKycWarningWithCountdown = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycWarningWithCountdown />, {
        closeOnClickMask: false,
        position: "center",
        animation: "fade",
        onClose: clearSearchAction,
    })
}

const openKycReminderHint = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderHint />, {
        closeOnClickMask: false,
        position: "center",
        animation: "fade",
        onClose: clearSearchAction,
    })
}

const openKycReminderInUserCenter = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderInUserCenter />, {
        closeOnClickMask: false,
        onClose: clearSearchAction,
    })
}

const openKycReminderInDeposit = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderInDeposit />, {
        closeOnClickMask: false,
        onClose: clearSearchAction,
    })
}

const openKycReminderInBeforeGame = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderInBeforeGame />, {
        closeOnClickMask: false,
        onClose: clearSearchAction,
    })
}

const openKycReminderInWithdraw = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderInWithdraw />, {
        closeOnClickMask: false,
        onClose: clearSearchAction,
    })
}

const openKycReminderHintBlockAccountLogin = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderHintBlockAccountLogin />, {
        closeOnClickMask: false,
        position: "center",
        animation: "fade",
        onClose: clearSearchAction,
    })
}

const openKycReminderHintUnderReview = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderHintUnderReview />, {
        closeOnClickMask: false,
        position: "center",
        animation: "fade",
        onClose: clearSearchAction,
    })
}

const openKycReminderInWelfare = async () => {
    await ModalSlideUpStaticMethods.open("kyc-status-modal", <KycReminderInWelfare />, {
        closeOnClickMask: false,
        onClose: clearSearchAction,
    })
}

// 預設 pendingCallback 實作
async function defaultPendingCallback(status: ExecuteStatus): Promise<boolean> {
    if (!BusinessSignals.isUserLogin.value) {
        return false
    }
    if (location.pathname.startsWith("/s-kyc")) {
        return false
    }
    switch (status.action) {
        case "withdraw": {
            if (status.currentStatus === "under_review") {
                await openKycReminderHintUnderReview()
                return false
            }
            await openKycReminderInWithdraw()
            return false
        }
        case "deposit": {
            await openKycReminderInDeposit()
            return false
        }
        case "depositmaya": {
            await openKycReminderInDeposit()
            return false
        }
        case "play": {
            await openKycReminderInBeforeGame()
            return false
        }
        case "welfare": {
            await openKycReminderInWelfare()
            return false
        }
        case "viewRecords": {
            logger.info("Cannot view records", {
                currentStatus: status.currentStatus,
                action: status.action,
            })
            return false
        }
        case "updateProfile": {
            logger.info("Cannot update profile", {
                currentStatus: status.currentStatus,
                action: status.action,
            })
            return false
        }
        case "afterLogin": {
            await openKycReminderHintBlockAccountLogin()
            return false
        }
    }
    return true
}

// 待執行的回調函數（可自定義）
export const pendingCallback = signal<(status: ExecuteStatus) => Promise<void>>()

// 計算當前動作是否被允許
export const isActionAllowed = computed(() => {
    const action = blockedAction.value
    const status = currentKycStatus.value
    if (!action?.type || !status) return false
    const rule = kycRulesConfig.value[action.type]
    let allowed = rule.byPassStatus.includes(status)
    if (rule.byPassCallback) {
        allowed = rule.byPassCallback(status)
    }
    return allowed
})

// signals 狀態集合
export const kycRequiredSignals = {
    sourceData,
    kycRulesConfig,
    currentKycStatus,
    blockedAction,
    pendingCallback,
    isActionAllowed,
    currentActionId,
}

interface ExecuteOptions {
    customPendingCallback?: (status: ExecuteStatus) => Promise<void>
}

/**
 * 執行 KYC 驗證
 */
export async function execute(
    action: KycRequiredAction,
    onStatus?: (status: ExecuteStatus) => Promise<void>,
    opts?: ExecuteOptions,
) {
    // 生成新的動作 ID
    currentActionId.value = Date.now()
    const actionId = currentActionId.value

    // // 更新資料
    await Promise.all([ApiFetchers.getKycV2(), ApiFetchers.refGetProfile()])

    // 檢查是否是最新的執行
    if (currentActionId.value !== actionId) {
        return
    }

    // 更新當前動作
    blockedAction.value = {
        id: actionId,
        type: action,
    }

    const status = currentKycStatus.value
    const rule = kycRulesConfig.value[action]
    const allowed = isActionAllowed.value

    // 建立狀態物件
    const executeStatus: ExecuteStatus = {
        id: actionId,
        allowed,
        currentStatus: status,
        requiredStatus: rule.byPassStatus,
        action,
        sourceData: sourceData.value,
    }

    if (allowed) {
        await onStatus?.(executeStatus)
    } else {
        // 優先使用自定義回調，其次是 signal 回調，最後是預設回調
        if (opts?.customPendingCallback) {
            await opts.customPendingCallback(executeStatus)
        } else if (pendingCallback.value) {
            await pendingCallback.value(executeStatus)
        } else {
            const result = await defaultPendingCallback(executeStatus)
            if (result) await onStatus?.(executeStatus)
        }
    }
}

/**
 * 清除特定動作的狀態
 */
export function clearAction(actionId: number) {
    if (blockedAction.value?.id === actionId) {
        blockedAction.value = null
    }
}

// 導出所有需要的內容
const kycRequiredMethods = {
    execute,
    clearAction,
    signals: kycRequiredSignals,
    kycStatus: currentKycStatus,
    openKycWarningWithCountdown,
    openKycReminderHint,
    openKycReminderInUserCenter,
    openKycReminderHintBlockAccountLogin,
    openKycReminderHintUnderReview,
    openKycReminderInWithdraw,
    openKycReminderInDeposit,
    openKycReminderInBeforeGame,
    openKycReminderInWelfare,
}

export default kycRequiredMethods
